/**
 * Shiny Hunt, Profile page (2026-09-03 owner ask; always-on and automated
 * since 2026-09-08).
 *
 * There is always a shiny to hunt, so the block is a constant fixture at
 * the top of the spine and fills itself from the feeds, top to bottom:
 *
 *   1. BOOSTED NOW: events boosting shiny odds (Community Day, Raid Day,
 *      GO Fest, a "shiny" bonus line) and the stated boost windows on an
 *      event's page ("Shiny Pidgey Sep 8 to 11", then Carvanha), each with
 *      its species as tiles. Habitat-scheduled fests draw a fest day at a
 *      time (lib/goFestHabitats.ts). Twelve hours of linger after each.
 *   2. NEW SHINIES: the debuts the pages announce (Shiny Flamigo), from the
 *      announcement to a week after the event.
 *   3. RAIDS: every current boss whose encounter can be shiny and whose
 *      shiny is still missing, one strip with the tier under each tile. A
 *      raid shiny is 1 in 20 for a 5★, Mega or Shadow boss, the best odds
 *      in the game.
 *   4. ROCKET: Giovanni's and the leaders' shadows whose shiny is missing,
 *      plus a pointer to the grunt pool in the Rocket radar lower down.
 *
 * The brain is lib/shinyHunt.ts (events, pure) and lib/rocketRadar.ts
 * (Rocket, pure); this file is the picture. Tiles come from ShinyGapTiles,
 * the same strip the Rocket radar draws, so a tap-to-add works identically.
 *
 * The event query reads the UPCOMING bucket too: liveness is decided here
 * against the device clock, so a window that opens at 10 a.m. local switches
 * on at 10 a.m. local whether or not the server's bucket has caught up.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowDown, ArrowRight, Clock, Eye, Sparkles, Swords } from "lucide-react";
import { InfoBlock } from "@/components/InfoBlock";
import { EvoLinesToggle } from "@/components/EvoLinesToggle";
import { useEvoLines } from "@/lib/evoLinesPref";
import { expandShinyTargetsToLines } from "@/lib/eventEvoLines";
import { speciesName } from "@/data/speciesNames";
import { EventSearchIcon } from "@/components/EventSearchIcon";
import { ShinyGapTiles, shinyTileStatus, type ShinyTileTarget } from "@/components/ShinyGapTiles";
import { bindDexSyncApiFetch } from "@/lib/dexStateStore";
import { apiRequestRaw } from "@/lib/queryClient";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import { armCalendarEvent } from "@/lib/calendarDayScroll";
import {
  shinyDebuts,
  shinyHuntEvents,
  type ShinyDebut,
  type ShinyHuntEvent,
  type ShinyHuntEventInput,
  type ShinyTarget,
} from "@/lib/shinyHunt";
import {
  habitatHourLabel,
  habitatScheduleFor,
  habitatSpeciesIds,
  habitatsAt,
  habitatsForDay,
  localDayKey,
  scheduleDays,
  windowStartMs,
  type GoFestHabitat,
  type GoFestHabitatSchedule,
} from "@/lib/goFestHabitats";
import { applyRaidDaySchedule } from "@/lib/raidDaySchedule";
import { RAID_TIERS_BIGGEST_FIRST, raidBossFormId, type RaidBossDto } from "@/lib/raidRoster";
import { rocketLeaderLineups, rocketLineupEncounters, type RocketLineupDto } from "@/lib/rocketRadar";
import { getDexEntryState } from "@/lib/dexStateStore";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { isVariantAvailable } from "@/lib/variantAvailability";

interface LiveEventApi extends ShinyHuntEventInput {
  image?: string;
  link?: string;
}
interface LiveEventsResponse {
  active?: LiveEventApi[];
  upcoming?: LiveEventApi[];
  recent?: LiveEventApi[];
}
interface RaidsResponse {
  tiers?: Partial<Record<string, RaidBossDto[]>>;
  meta?: { fetchedAt?: string | null };
}
interface RocketResponse {
  lineups?: RocketLineupDto[];
  meta?: { fetchedAt?: string | null };
}

export const ROCKET_RADAR_DOM_ID = "section-rocket-radar";

/** "Sat" / "Sun" for a schedule day, local. */
function dayLabel(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short" });
}

/** "Sep 8" style local date. */
function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** "Sep 8, 10:00" style local instant, the minutes dropped on the hour. */
function shortWhen(ms: number): string {
  const d = new Date(ms);
  const date = shortDate(ms);
  const h = d.getHours();
  const m = d.getMinutes();
  const time = `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
  return `${date}, ${time}`;
}

function toTargets(list: readonly ShinyTarget[]): ShinyTileTarget[] {
  return list.map((t) => {
    const formId = t.formSuffix ? raidBossFormId({ speciesId: t.speciesId, formSuffix: t.formSuffix }) : null;
    return { speciesId: t.speciesId, ...(formId ? { formId } : {}) };
  });
}

/**
 * Evo (2026-10-20 owner ask, "Shiny hunt also needs to get the evo toggle"):
 * with the device-wide switch on, every tile brings the stages it evolves
 * into whose shiny exists, so a shiny Sneasel hunt shows the Weavile gap too.
 */
function widen(targets: ShinyTileTarget[], evoLines: boolean): ShinyTileTarget[] {
  return evoLines ? expandShinyTargetsToLines(targets) : targets;
}

/** Caption for a tile the Evo switch added: the featured stage it comes from. */
function viaCaption(t: ShinyTileTarget): string | undefined {
  const via = (t as ShinyTileTarget & { via?: number }).via;
  return via != null ? `via ${speciesName(via)}` : undefined;
}

function SectionLine({ icon: Icon, children, testId }: { icon: typeof Sparkles; children: ReactNode; testId: string }) {
  return (
    <p
      className="text-[10px] font-black uppercase tracking-[0.14em] inline-flex items-center gap-1.5"
      style={{ color: "var(--text-ink-faint)" }}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5" style={{ color: "var(--accent-fill)" }} aria-hidden />
      {children}
    </p>
  );
}

export function ShinyHuntBlock() {
  // Same freshness the raid panel uses: the global defaults never refetch,
  // and a fest morning must not depend on yesterday's fetch.
  const liveQ = useQuery<LiveEventsResponse>({
    queryKey: [eventsLiveKey()],
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  });
  const raidsQ = useQuery<RaidsResponse>({
    queryKey: ["/api/events/raids"],
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  });
  const rocketQ = useQuery<RocketResponse>({
    queryKey: ["/api/events/rocket"],
    staleTime: 5 * 60_000,
    refetchInterval: 30 * 60_000,
  });
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  // Commits here must reach the server backup even when the Pokédex page has
  // not mounted this session (idempotent; mirrors the Raid Radar).
  useEffect(() => {
    bindDexSyncApiFetch(apiRequestRaw);
  }, []);
  const [evoLines] = useEvoLines();

  const allEvents = useMemo(
    () => [...(liveQ.data?.active ?? []), ...(liveQ.data?.upcoming ?? []), ...(liveQ.data?.recent ?? [])],
    [liveQ.data],
  );
  const hunts = useMemo(() => shinyHuntEvents(allEvents, now), [allEvents, now]);
  const debuts = useMemo(() => shinyDebuts(allEvents, now), [allEvents, now]);

  // Raids: the day schedule (raidDaySchedule.ts) outruns the feed on a Raid
  // Day, the same way the raid panel reads it. ONE strip, biggest tier
  // first, each tile captioned with its tier under it (owner direction
  // 2026-09-08, second pass: no per-tier heading lines).
  const raids = useMemo(() => {
    const tiers = applyRaidDaySchedule(raidsQ.data?.tiers ?? {}, now);
    const targets: ShinyTileTarget[] = [];
    const captions = new Map<string, string>();
    for (const tier of RAID_TIERS_BIGGEST_FIRST) {
      for (const boss of tiers[tier] ?? []) {
        if (!boss.canBeShiny || typeof boss.speciesId !== "number") continue;
        const formId = raidBossFormId(boss) ?? undefined;
        const key = `${boss.speciesId}:${formId ?? ""}`;
        if (captions.has(key)) continue;
        captions.set(key, tier === "mega" ? "Mega raid" : `${tier}★ raid`);
        targets.push({ speciesId: boss.speciesId, ...(formId ? { formId } : {}) });
      }
    }
    const widened = widen(targets, evoLines);
    for (const t of widened) {
      const caption = viaCaption(t);
      const key = `${t.speciesId}:${t.formId ?? ""}`;
      if (caption && !captions.has(key)) captions.set(key, caption);
    }
    const gapCount = widened.filter((t) => shinyTileStatus(t) === "gap").length;
    return { targets: widened, captions, gapCount };
  }, [raidsQ.data, now, evoLines]);
  const raidsKey = raidsQ.data?.meta?.fetchedAt ?? (raidsQ.data ? "raids" : "");

  // Rocket: Giovanni and the leaders, shiny-eligible encounters only, as one
  // strip captioned with who hands the shadow over; the grunt pool is
  // pointed at, not drawn twice on one page.
  const rocket = useMemo(() => {
    const lineups = rocketQ.data?.lineups ?? [];
    const dex = {
      isPresent: (id: number, formId?: string) => getDexEntryState("shiny", id, formId ?? null)?.state === "present",
      isAvailable: (id: number, formId?: string) =>
        formId ? isEventFormAvailable("shiny", formId, id) : isVariantAvailable("shiny", id),
    };
    const targets: ShinyTileTarget[] = [];
    const captions = new Map<string, string>();
    for (const lineup of rocketLeaderLineups(lineups)) {
      for (const e of rocketLineupEncounters(lineup, dex)) {
        if (!e.canBeShiny) continue;
        const key = `${e.speciesId}:${e.formId ?? ""}`;
        if (captions.has(key)) continue;
        captions.set(key, lineup.name);
        targets.push({ speciesId: e.speciesId, ...(e.formId ? { formId: e.formId } : {}) });
      }
    }
    const widened = widen(targets, evoLines);
    for (const t of widened) {
      const caption = viaCaption(t);
      const key = `${t.speciesId}:${t.formId ?? ""}`;
      if (caption && !captions.has(key)) captions.set(key, caption);
    }
    const gapCount = widened.filter((t) => shinyTileStatus(t) === "gap").length;
    return { targets: widened, captions, gapCount };
  }, [rocketQ.data, evoLines]);
  const rocketKey = rocketQ.data?.meta?.fetchedAt ?? (rocketQ.data ? "rocket" : "");

  const loading = liveQ.isLoading || raidsQ.isLoading || rocketQ.isLoading;
  const hasAnything = hunts.length > 0 || debuts.length > 0 || raids.targets.length > 0 || rocket.targets.length > 0;

  return (
    <InfoBlock
      icon={Sparkles}
      title="Shiny Hunt"
      subtitle="Still missing shiny · tap to choose"
      trailing={<EvoLinesToggle testId="shiny-hunt-evo" />}
      data-testid="profile-shiny-hunt"
    >
      {!hasAnything ? (
        <div className="rounded-2xl p-4 surface-elev text-center" data-testid="shiny-hunt-empty">
          <p className="text-[11px] text-muted-foreground">
            {loading ? "Checking the feeds for shiny odds." : "The feeds are quiet. Pull down to try again."}
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {hunts.length > 0 && (
            <div className="space-y-4" data-testid="shiny-hunt-boosted">
              <SectionLine icon={Sparkles} testId="shiny-hunt-boosted-heading">
                Boosted odds
              </SectionLine>
              {hunts.map((h) => (
                <ShinyHuntEventCard key={`${h.event.id}:${h.window?.start ?? "event"}`} hunt={h} now={now} />
              ))}
            </div>
          )}

          {debuts.length > 0 && (
            <div className="space-y-3" data-testid="shiny-hunt-debuts">
              <SectionLine icon={Eye} testId="shiny-hunt-debuts-heading">
                New shinies
              </SectionLine>
              {debuts.map((d) => (
                <ShinyDebutCard key={d.event.id} debut={d} />
              ))}
            </div>
          )}

          {raids.targets.length > 0 && (
            <div className="space-y-2" data-testid="shiny-hunt-raids">
              <SectionLine icon={Swords} testId="shiny-hunt-raids-heading">
                Raids · shiny possible
              </SectionLine>
              {raids.gapCount > 0 ? (
                <ShinyGapTiles
                  listKey={`${raidsKey}:raids${evoLines ? ":evo" : ""}`}
                  targets={raids.targets}
                  heading={false}
                  gapsOnly
                  captionFor={(t) => raids.captions.get(`${t.speciesId}:${t.formId ?? ""}`)}
                  testIdPrefix="shiny-hunt-raids"
                />
              ) : (
                <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="shiny-hunt-raids-done">
                  Every boss that can be shiny already is in your Shiny Dex.
                </p>
              )}
            </div>
          )}

          {rocket.targets.length > 0 && (
            <RocketLeadersHunt
              targets={rocket.targets}
              captions={rocket.captions}
              gapCount={rocket.gapCount}
              listKey={`${rocketKey}${evoLines ? ":evo" : ""}`}
            />
          )}

        </div>
      )}
    </InfoBlock>
  );
}

function windowLabel(hunt: ShinyHuntEvent): string {
  if (hunt.window) {
    if (hunt.preview) return `From ${shortWhen(hunt.window.start)}`;
    if (hunt.live) return `Until ${shortWhen(hunt.window.end)}`;
    return "Ended, still counting";
  }
  if (hunt.preview) return "Preview";
  return hunt.live ? "Boosted now" : "Ended, still counting";
}

function ShinyHuntEventCard({ hunt, now }: { hunt: ShinyHuntEvent<LiveEventApi>; now: number }) {
  const [, setLocation] = useLocation();
  const ev = hunt.event;
  const [evoLines] = useEvoLines();
  const schedule = hunt.window ? null : habitatScheduleFor(ev.id);
  // The count is everything the entry boosts: its habitats' spawns plus
  // whatever the feed listed (the raid bosses), no species twice.
  const boostedCount = schedule
    ? new Set([...habitatSpeciesIds(schedule), ...hunt.speciesIds]).size
    : hunt.targets.length;
  const listKey = `${ev.id}:${hunt.window?.start ?? "event"}`;
  // The Evo switch is part of the frozen list's identity: a flip rebuilds it.
  // The test ids keep the plain key.
  const frozenKey = `${listKey}${evoLines ? ":evo" : ""}`;

  return (
    <div data-testid={`shiny-hunt-${ev.id}${hunt.window ? `-${hunt.window.start}` : ""}`}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            armCalendarEvent(ev.id);
            setLocation("/calendar");
          }}
          className="min-w-0 flex-1 text-left"
          data-testid={`shiny-hunt-open-${ev.id}`}
        >
          <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
            {ev.name}
          </p>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1"
            style={{ color: hunt.live ? "hsl(var(--shiny))" : "var(--text-tertiary)" }}
            data-testid={`shiny-hunt-state-${ev.id}`}
          >
            {hunt.preview ? <Clock className="w-3 h-3" aria-hidden /> : null}
            {windowLabel(hunt)} · {boostedCount} boosted
          </p>
        </button>
        <EventSearchIcon eventId={ev.id} spawns={ev.spawns} />
      </div>

      {schedule && <HabitatHunt eventId={ev.id} schedule={schedule} now={now} preview={hunt.preview} />}

      {/* The feed's own species: for a fest, its raid bosses. */}
      <ShinyGapTiles
        listKey={frozenKey}
        targets={widen(toTargets(hunt.targets), evoLines)}
        heading={false}
        captionFor={(t) => viaCaption(t) ?? (schedule ? "Mega raid" : undefined)}
        testIdPrefix={`shiny-hunt-${listKey}`}
      />
    </div>
  );
}

function ShinyDebutCard({ debut }: { debut: ShinyDebut<LiveEventApi> }) {
  const [, setLocation] = useLocation();
  const ev = debut.event;
  const [evoLines] = useEvoLines();
  const state = debut.upcoming ? `From ${shortDate(debut.startMs)}` : debut.live ? "Out now" : "Released";
  return (
    <div data-testid={`shiny-hunt-debut-${ev.id}`}>
      <button
        type="button"
        onClick={() => {
          armCalendarEvent(ev.id);
          setLocation("/calendar");
        }}
        className="w-full text-left"
        data-testid={`shiny-hunt-debut-open-${ev.id}`}
      >
        <p className="text-[12.5px] font-bold truncate" style={{ color: "var(--text-primary)" }}>
          {ev.name}
        </p>
        <p
          className="text-[10px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1"
          style={{ color: debut.live ? "hsl(var(--shiny))" : "var(--text-tertiary)" }}
          data-testid={`shiny-hunt-debut-state-${ev.id}`}
        >
          {debut.upcoming ? <Clock className="w-3 h-3" aria-hidden /> : <Sparkles className="w-3 h-3" aria-hidden />}
          {state} · shiny debut
        </p>
      </button>
      <ShinyGapTiles
        listKey={`debut:${ev.id}${evoLines ? ":evo" : ""}`}
        targets={widen(toTargets(debut.targets), evoLines)}
        heading={false}
        captionFor={(t) => viaCaption(t)}
        testIdPrefix={`shiny-hunt-debut-${ev.id}`}
      />
    </div>
  );
}

/**
 * Giovanni and the leaders whose shadow can be shiny, gaps only in one
 * captioned strip, and the pointer to the grunt pool drawn in full by the
 * Rocket radar lower down.
 */
function RocketLeadersHunt({
  targets,
  captions,
  gapCount,
  listKey,
}: {
  targets: ShinyTileTarget[];
  captions: ReadonlyMap<string, string>;
  gapCount: number;
  listKey: string;
}) {
  const [, setLocation] = useLocation();
  return (
    <div className="space-y-2" data-testid="shiny-hunt-rocket">
      <SectionLine icon={Swords} testId="shiny-hunt-rocket-heading">
        Rocket leaders · shiny shadows
      </SectionLine>
      {gapCount > 0 ? (
        <ShinyGapTiles
          listKey={`${listKey}:leaders`}
          targets={targets}
          heading={false}
          gapsOnly
          captionFor={(t) => captions.get(`${t.speciesId}:${t.formId ?? ""}`)}
          testIdPrefix="shiny-hunt-rocket-leaders"
        />
      ) : (
        <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="shiny-hunt-rocket-done">
          The leaders' shadows are all shiny in your Nexus Dex already.
        </p>
      )}
      <button
        type="button"
        onClick={() => document.getElementById(ROCKET_RADAR_DOM_ID)?.scrollIntoView({ behavior: "smooth", block: "start" })}
        className="inline-flex items-center gap-1 text-[11px] font-bold"
        style={{ color: "var(--brand-green-on-light)", minHeight: 32 }}
        data-testid="shiny-hunt-rocket-more"
      >
        Grunt pool in the Rocket radar
        <ArrowDown className="w-3 h-3" aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => setLocation("/rocket")}
        className="ml-3 inline-flex items-center gap-1 text-[11px] font-bold"
        style={{ color: "var(--brand-green-on-light)", minHeight: 32 }}
        data-testid="shiny-hunt-rocket-page"
      >
        Every line-up and your best three
        <ArrowRight className="w-3 h-3" aria-hidden />
      </button>
    </div>
  );
}

/**
 * The habitats, a DAY at a time (owner direction 2026-09-04, second pass):
 * Saturday's four on Saturday, Sunday's four on Sunday, all visible at
 * once, each under a line in the habitat's own colour that carries its two
 * hours. The live habitat is lit; a preview (before the fest) opens on the
 * first day with a switch to browse the other.
 */
function HabitatHunt({
  eventId,
  schedule,
  now,
  preview,
}: {
  eventId: string;
  schedule: GoFestHabitatSchedule;
  now: number;
  preview: boolean;
}) {
  const rotation = habitatsAt(schedule, now);
  const current = rotation.current;
  const days = useMemo(() => scheduleDays(schedule), [schedule]);
  const today = localDayKey(now);
  // The fest day the block opens on: today while the fest is on, else the
  // first day still to come (a preview), else the last day (the linger).
  const defaultDay = days.includes(today) ? today : days.find((d) => d > today) ?? days[days.length - 1];
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const day = pickedDay && days.includes(pickedDay) ? pickedDay : defaultDay;
  const habitats = useMemo(() => habitatsForDay(schedule, day), [schedule, day]);

  return (
    <div className="mt-2.5 space-y-3" data-testid={`shiny-hunt-habitats-${eventId}`} data-day={day} data-preview={preview ? "true" : "false"}>
      {days.length > 1 && (
        <div className="flex gap-1.5" role="tablist" aria-label="Fest day" data-testid={`shiny-hunt-days-${eventId}`}>
          {days.map((d) => {
            const on = d === day;
            return (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => setPickedDay(d)}
                data-testid={`shiny-hunt-day-${eventId}-${d}`}
                className="rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em]"
                style={{
                  background: on ? "var(--accent-fill)" : "var(--surface-sunken)",
                  border: `1px solid ${on ? "var(--accent-fill)" : "var(--border-subtle)"}`,
                  color: on ? "var(--text-on-accent)" : "var(--text-secondary)",
                  minHeight: 30,
                }}
              >
                {dayLabel(d)}
                {d === today ? " · today" : ""}
              </button>
            );
          })}
        </div>
      )}
      {habitats.map(({ habitat, windows }) => {
        const live = current?.habitat.key === habitat.key && current.window.day === day;
        const over = !live && windows.every((w) => windowStartMs(w) + 60 * 60 * 1000 <= now);
        return (
          <HabitatTiles
            key={habitat.key}
            eventId={eventId}
            habitat={habitat}
            hours={windows.map((w) => habitatHourLabel(w.startHour))}
            live={live}
            over={over}
          />
        );
      })}
    </div>
  );
}

/**
 * One habitat: a full-width line in the habitat's colour carrying its
 * hours and name, then its species as tiles. Lit while it is on.
 */
function HabitatTiles({
  eventId,
  habitat,
  hours,
  live,
  over,
}: {
  eventId: string;
  habitat: GoFestHabitat;
  hours: string[];
  live: boolean;
  over: boolean;
}) {
  const [evoLines] = useEvoLines();
  return (
    <div
      data-testid={`shiny-hunt-habitat-${eventId}-${habitat.key}`}
      data-live={live ? "true" : "false"}
      style={{ opacity: over ? 0.6 : 1 }}
    >
      <p
        className="flex items-center gap-2 rounded-lg px-2 py-1 text-[10.5px] font-black uppercase tracking-[0.12em]"
        style={{
          background: live ? habitat.color : `color-mix(in srgb, ${habitat.color} 16%, transparent)`,
          border: `1px solid ${habitat.color}`,
          color: live ? "#fff" : habitat.color,
          boxShadow: live ? `0 0 0 2px color-mix(in srgb, ${habitat.color} 35%, transparent)` : "none",
        }}
        data-testid={`shiny-hunt-habitat-line-${eventId}-${habitat.key}`}
      >
        <span className="tabular-nums shrink-0 inline-flex items-center gap-1">
          {live ? <Sparkles className="w-3 h-3" aria-hidden /> : <Clock className="w-3 h-3" aria-hidden />}
          {hours.join(" · ")}
        </span>
        <span className="min-w-0 flex-1 truncate text-right">{habitat.name}{live ? " · on now" : ""}</span>
      </p>
      <ShinyGapTiles
        listKey={`${eventId}-${habitat.key}${evoLines ? ":evo" : ""}`}
        targets={widen(habitat.speciesIds.map((speciesId) => ({ speciesId })), evoLines)}
        heading={false}
        captionFor={(t) => viaCaption(t)}
        testIdPrefix={`shiny-hunt-${eventId}-${habitat.key}`}
      />
    </div>
  );
}
